/**
 * @file StageListRow.tsx
 * @description Строка списка стадий в настройках (phase19.31, разбор A:
 * «компактный список 56: ⠿ · ● название · «N заказов · срок 24 ч»»).
 *
 * Правка — по нажатию на строку (без отдельного значка-карандаша), удаление —
 * в «⋯» последним пунктом. Цвет магазина — только точкой (SC28), код стадии
 * не показываем: он нужен системе, а не владельцу. Перетаскивание порядка —
 * за ручку ⠿ (сенсор @dnd-kit с порогом, клик по строке не путается с
 * перетаскиванием).
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";

import { stageCaption, type StageListItem } from "./stage-list-types";
import { STAGE_DOT_FALLBACK } from "../board/board-types";
import { Button } from "../../ui/button";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { StatusPill } from "../../ui/StatusPill";
import { safeStageColor } from "../../../lib/stage-color";
import { cn } from "@/lib/utils";

interface Props<T extends StageListItem> {
  stage: T;
  count: number;
  countWords: [string, string, string];
  canConfig: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export function StageListRow<T extends StageListItem>({ stage, count, countWords, canConfig, onEdit, onDelete }: Props<T>) {
  const sortable = useSortable({ id: stage.id, disabled: !canConfig });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };
  const body = (
    <>
      <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: safeStageColor(stage.color) ?? STAGE_DOT_FALLBACK }} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-2">
          <span className="type-body truncate font-medium text-on-surface">{stage.name}</span>
          {stage.isActive ? null : <StatusPill tone="neutral">В архиве</StatusPill>}
        </span>
        <span className="type-caption truncate text-on-surface-variant">{stageCaption(count, countWords, stage.slaHours)}</span>
      </span>
    </>
  );

  return (
    <li ref={sortable.setNodeRef} style={style} className="flex min-h-14 items-center gap-1 pr-2">
      {canConfig ? (
        <Button
          variant="ghost"
          size="icon-sm"
          {...sortable.attributes}
          {...sortable.listeners}
          aria-label={`Перетащить стадию «${stage.name}»`}
          className="cursor-grab touch-none text-on-surface-variant active:cursor-grabbing"
        >
          <GripVertical className="size-4" />
        </Button>
      ) : null}
      {canConfig ? (
        <button
          type="button"
          onClick={onEdit}
          className={cn(
            "flex min-h-14 min-w-0 flex-1 items-center gap-3 rounded-lg px-2 text-left outline-none",
            "transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {body}
        </button>
      ) : (
        <div className="flex min-h-14 min-w-0 flex-1 items-center gap-3 px-4">{body}</div>
      )}
      {canConfig ? (
        <RowActionsMenu
          size="compact"
          title={`Стадия «${stage.name}»`}
          ariaLabel={`Действия со стадией «${stage.name}»`}
          actions={[
            { key: "edit", label: "Изменить", icon: Pencil, run: onEdit },
            // На стадии есть записи — удалить нельзя (сервер ответит отказом): архивируют в окне правки.
            { key: "delete", label: count > 0 ? "Удалить (сначала уберите записи)" : "Удалить", icon: Trash2, danger: true, disabled: count > 0, run: onDelete },
          ]}
        />
      ) : null}
    </li>
  );
}
